import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import List, { STATE } from "../../List/index";
import SecondaryPagination from "../../SecondaryPagination/SecondaryPagination";
import SearchBox from "../SearchBox";
import { format } from "date-fns";
import View from "./View";
import useTickets from "../../../hooks/useTickets";
import AppContext from "../../../AppContext";

const columns = [
  {
    display: "#",
    key: "no",
    textAlign: "left",
    maxWidth: 60,
  },
  {
    display: "Category",
    key: "category",
    textAlign: "left",
    maxWidth: 60,
  },
  {
    display: "Topic",
    key: "topic",
    textAlign: "left",
    maxWidth: 120,
  },
  {
    display: "Description",
    key: "desc",
    textAlign: "left",
    maxWidth: 300,
  },
  {
    display: "Opened on",
    key: "createdOn",
    textAlign: "left",
    maxWidth: 80,
  },
];

const Tickets = ({ mode }) => {
  const { user: staff } = useContext(AppContext);
  const [dataState, setDataState] = useState(STATE.LOADING);
  const [tickets, setTickets] = useState(undefined);
  const [metadata, setMetadata] = useState(undefined);
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState({});
  const [selected, setSelected] = useState(undefined);
  const [defaultFilter, setDefaultFilter] = useState(undefined);

  const searchRef = useRef(null);

  // * Toggles
  const [toggleView, setToggleView] = useState(false);
  const { queryTickets } = useTickets();

  useEffect(() => {
    changeDefaultFilter();
    return () => {};
  }, []);

  const changeDefaultFilter = () => {
    if (!mode) return;

    if (mode === "all") {
      setDefaultFilter({
        deleted: false,
      });
      return;
    }

    if (mode === "open") {
      setDefaultFilter({
        closed: false,
        staff: {
          $eq: null,
        },
        deleted: false,
      });
      return;
    }

    if (mode === "closed") {
      setDefaultFilter({
        closed: true,
        deleted: false,
      });
      return;
    }

    // * My ticket filter
    setDefaultFilter({
      closed: false,
      staff: staff._id,
      deleted: false,
    });
  };

  useEffect(() => {
    searchRef.current?.clearFilter();
    setFilter({});

    changeDefaultFilter();
  }, [mode]);

  useEffect(() => {
    if (!defaultFilter) return;
    fetchData();
  }, [defaultFilter, filter, limit, page]);

  // * Fetch data
  const fetchData = async () => {
    setDataState(STATE.LOADING);
    const { data, _metadata } = await queryTickets(
      { ...filter, ...defaultFilter },
      page,
      limit,
      null,
      { createdOn: -1 }
    );

    let _data = data.map(({ _id, no, topic, desc, category, createdOn }) => {
      const _createdOn = format(new Date(createdOn), "dd/MM/yyyy");

      return {
        _id,
        no: (no ?? 1).toString().padStart(6, "0"),
        topic,
        desc,
        // desc: <span className="admin-tickets-list-desc">{desc}</span>,
        category,
        createdOn: _createdOn,
      };
    });

    setTickets(_data);
    setMetadata(_metadata);
    setDataState(STATE.IDLE);
  };

  const handleSelected = ({ _id }) => {
    setSelected(_id);
    setToggleView(true);
  };

  const handleCloseView = () => {
    setSelected(undefined);
    setToggleView(false);
    fetchData();
  };

  const handleOnPageChange = (page) => setPage(page);

  const handleOnLimitChange = (limit) => setLimit(limit);

  const handleSearchChange = (value) => {
    if (!value) {
      setFilter({});
      return;
    }

    setPage(1);
    setFilter({
      $or: [
        {
          no: isNaN(value) ? 0 : +value,
        },
        {
          topic: { $regex: value },
        },
      ],
    });
  };

  return (
    <>
      <div className="admin-users">
        <div className="admin-users-header">
          <SearchBox
            ref={searchRef}
            placeholder="Ticket No or Topic"
            onChange={handleSearchChange}
          />
        </div>
        <List
          contentState={dataState}
          columns={columns}
          dataSource={tickets}
          viewable={true}
          onRecordClick={handleSelected}
        >
          <SecondaryPagination
            limits={[10, 50]}
            defaultPage={metadata?.currentPage || 1}
            pages={metadata?.totalPages || 1}
            onPageChange={handleOnPageChange}
            onLimitChange={handleOnLimitChange}
          />
        </List>
      </div>
      {toggleView && <View ticketId={selected} onClose={handleCloseView} />}
    </>
  );
};

Tickets.propTypes = {
  mode: PropTypes.oneOf(["open", "closed", "my"]),
};

export default Tickets;
